import { faMaximize, faMinimize, IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import Text from '../Text';
import Flex from '../Flex';
import useOnClickOutside from 'hooks/useCloseOnClickOutside';
import CloseButton from '../CloseButton';
import IconButton from '../IconButton';

interface Props {
    show: boolean;
    blur?: boolean;
    tint?: boolean;
    children?: React.ReactNode;
    portal?: boolean;
}

/*
  Passing portal will render Modal in document.body
  be aware of event propagation for example if parent component has
  an event on "click outside" the event will be triggered when clicking anywhere but the parent

*/
const Modal = React.forwardRef<HTMLDivElement, Props>(
    ({ show, blur = false, tint = false, children, portal = false, ...props }, ref) => {
        const renderModal = (
            <StyledModalWrapper>
                <StyledModal show={show} {...props} ref={ref}>
                    {children}
                </StyledModal>
                <StyledPageTint tint={show && tint} />
                <StyledPageBlur blur={show && blur} />
            </StyledModalWrapper>
        );
        return portal ? createPortal(renderModal, document.body) : renderModal;
    },
);
export type ModalAction = {
    id?: string;
    icon: IconDefinition;
    label?: string;
    onClick: () => void;
    disabled?: boolean;
    title?: string;
};

// Similar looks as Modal but more selfcontained rendering close and fullscreenbuttons if wanted.
interface DialogProps {
    children: React.ReactNode;
    onClose?: () => void;
    onFullscreen?: (fullscreen: boolean) => void;
    additionalActions?: ModalAction[];
    closeOutside?: boolean;
    blur?: boolean;
    tint?: boolean;
    width?: string;
    height?: string;
    maxWidth?: string;
    maxHeight?: string;
    portal?: boolean;
    header?: string | null;
    overFlow?: string;
    startFullscreen?: boolean;
}
export const Dialog = ({
    children,
    onClose,
    onFullscreen,
    closeOutside,
    blur,
    tint,
    width,
    height,
    maxWidth,
    maxHeight,
    portal = false,
    header,
    additionalActions,
    overFlow = 'auto',
    startFullscreen = false,
}: DialogProps) => {
    const ref = useOnClickOutside(() => onClose?.(), closeOutside ?? false);
    const [fullscreen, setFullscreen] = useState<boolean>(startFullscreen);

    const modalRef = useCallback((node: HTMLDivElement | null) => {
        if (node !== null) {
            disableBodyScroll(node);
        }
    }, []);

    useEffect(() => {
        return () => clearAllBodyScrollLocks();
    }, []);

    const renderModal = (
        <StyledDialog
            className="exclude-click-listener"
            tint={tint}
            blur={blur}
            ref={modalRef}
            align="center"
            justify="center"
        >
            <DialogInner
                fullscreen={fullscreen}
                ref={ref}
                width={width}
                height={height}
                maxWidth={maxWidth}
                maxHeight={maxHeight}
                overFlow={overFlow}
            >
                {header && (
                    <Header className="mx-3">
                        <Text as="label" fontSize={20} bold>
                            {header}
                        </Text>
                    </Header>
                )}
                <Actions>
                    {additionalActions &&
                        additionalActions.map((action, index) => (
                            <ActionButton
                                id={action.id}
                                key={index}
                                icon={action.icon}
                                onClick={action.onClick}
                                color="text"
                                bgColor="fillWhite"
                                className="mr-2"
                                title={action.title}
                                disabled={action.disabled}
                            >
                                {action.label}
                            </ActionButton>
                        ))}
                    {onFullscreen && (
                        <FullScreenButton
                            className="mr-2"
                            onClick={() => {
                                onFullscreen(!fullscreen);
                                setFullscreen(!fullscreen);
                            }}
                            icon={fullscreen ? faMinimize : faMaximize}
                            color="text"
                            bgColor="fillWhite"
                        />
                    )}
                    {onClose && <CloseButton onClose={() => onClose()} shadow={false} />}
                </Actions>

                {children}
            </DialogInner>
        </StyledDialog>
    );
    return portal ? createPortal(renderModal, document.body) : renderModal;
};

export default Modal;

const StyledModalWrapper = styled.div`
    overscroll-behavior: contain;
`;

const ModalStyles = css`
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    z-index: 9998; // Above everything exept alerts and toasts.
    transition: opacity 0.2s ease, visibility 0.2s ease;
`;

const StyledDialog = styled(Flex)<{ tint?: boolean; blur?: boolean }>`
    ${ModalStyles}
    ${({ blur, tint }) =>
        (blur || tint) && `backdrop-filter: ${blur ? 'blur(3px)' : ''} ${tint ? 'brightness(0.5)' : ''};`}
`;

const DialogInner = styled.div<{
    fullscreen: boolean;
    width?: string;
    height?: string;
    maxWidth?: string;
    maxHeight?: string;
    overFlow?: string;
}>`
    position: relative;
    background: ${({ theme }) => theme.colors.fillWhite};
    overflow: ${({ overFlow }) => overFlow ?? 'auto'};
    height: 100%;
    width: 100%;

    ${({ theme }) => theme.media.lg} {
        ${({ fullscreen, width, height, maxWidth, maxHeight }) =>
            !fullscreen &&
            `
        width: ${width ?? 'auto'};
        height: ${height ?? 'auto'};
        max-width: ${maxWidth ?? 'none'};
        max-height: ${maxHeight ?? 'none'};
      `}
    }
`;

const StyledModal = styled.div<{ show: boolean }>`
  ${ModalStyles}
  pointer-events: ${({ show }) => (show ? 'all' : 'none')};

  ${({ show }) =>
      !show &&
      `
    opacity: 0;
    visibility: hidden;
  `}
`;

const StyledPageTint = styled.div<{ tint: boolean }>`
    width: 100%;
    height: 100%;
    opacity: ${({ tint }) => (tint ? 0.5 : 0)};
    transition: opacity 0.5s ease;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: #00000081;
    z-index: 104;
    pointer-events: none;
`;

const StyledPageBlur = styled.div<{ blur: boolean }>`
    width: 100%;
    height: 100%;
    opacity: ${({ blur }) => (blur ? 1 : 0)};
    transition: opacity 0.5s ease;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    backdrop-filter: blur(3px);
    background-color: #00000081;
    z-index: 104;
    pointer-events: none;
`;

export const ModalInner = styled.div`
    display: flex;
    overflow-y: auto;
    flex-direction: column;
    align-items: center;
    background: white;
    max-height: 100%;
    max-width: max-content;
    padding: 50px 40px;
    box-shadow: 0px 6px 15px 5px rgba(163, 163, 163, 0.19);
`;

const Header = styled.div`
    z-index: 2;
    top: 8px;
    left: 0;
    line-height: 34px;
    position: absolute;
    width: auto;
`;

const Actions = styled(Flex)`
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
    z-index: 2;
`;

const FullScreenButton = styled(IconButton)`
    ${({ theme }) => theme.media.lgBelow} {
        display: none;
    }
`;
const ActionButton = styled(IconButton)`
    ${({ theme }) => theme.media.lgBelow} {
        span {
            display: none;
        }
    }
`;
